import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #autoCorrectTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Prevent form submission when there are quantity validation errors
    const cartForm = this.querySelector('#cart-form') || document.getElementById('cart-form');
    if (cartForm) {
      cartForm.addEventListener('submit', this.#handleFormSubmit);
    }

    // Validate immediately as user types (not just after AJAX completes)
    this.addEventListener('input', this.#handleInputChange);

    // Validate quantity rules on initial load
    this.#validateQuantityRules();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Remove form submit listener
    const cartForm = this.querySelector('#cart-form') || document.getElementById('cart-form');
    if (cartForm) {
      cartForm.removeEventListener('submit', this.#handleFormSubmit);
    }

    // Remove input listener
    this.removeEventListener('input', this.#handleInputChange);

    // Clear any pending auto-correct timeout
    if (this.#autoCorrectTimeout) {
      clearTimeout(this.#autoCorrectTimeout);
    }
  }

  /**
   * Handles input changes - validates immediately as user types.
   * @param {Event} event - The input event.
   */
  #handleInputChange = (event) => {
    const target = event.target;
    // Only validate for quantity inputs
    if (!(target instanceof HTMLInputElement) || target.type !== 'number') return;

    // Run validation immediately (without auto-correct delay)
    this.#validateQuantityRulesImmediate();
  };

  /**
   * Validates quantity rules immediately without auto-correct.
   * Used for real-time feedback as user types.
   */
  #validateQuantityRulesImmediate() {
    const rows = this.querySelectorAll('[ref="cartItemRows[]"]');
    let hasErrors = false;

    rows.forEach((row, index) => {
      if (!(row instanceof HTMLElement)) return;

      const minQty = parseInt(row.dataset.minQuantity, 10) || 1;
      const increment = parseInt(row.dataset.increment, 10) || 1;
      const input = row.querySelector('input[type="number"]');
      const qty = parseInt(input instanceof HTMLInputElement ? input.value : '0', 10) || 0;
      const errorEl = this.querySelector(`[ref="cartItemQuantityError-${index + 1}"]`);

      // Skip items with no pricing tier restrictions
      if (minQty === 1 && increment === 1) {
        if (errorEl instanceof HTMLElement) {
          errorEl.classList.add('hidden');
        }
        row.classList.remove('cart-items__table-row--error');
        return;
      }

      // Skip empty inputs (user is still typing)
      if (!input || (input instanceof HTMLInputElement && input.value === '')) {
        if (errorEl instanceof HTMLElement) {
          errorEl.classList.add('hidden');
        }
        row.classList.remove('cart-items__table-row--error');
        return;
      }

      // Skip removed items (qty=0 means removing)
      if (qty === 0) {
        if (errorEl instanceof HTMLElement) {
          errorEl.classList.add('hidden');
        }
        row.classList.remove('cart-items__table-row--error');
        return;
      }

      const isValid = qty >= minQty && (qty === minQty || (minQty % increment !== 0 ? qty % increment === 0 : (qty - minQty) % increment === 0));

      if (!isValid) {
        hasErrors = true;
        if (errorEl instanceof HTMLElement) {
          errorEl.classList.remove('hidden');
        }
        row.classList.add('cart-items__table-row--error');
      } else {
        if (errorEl instanceof HTMLElement) {
          errorEl.classList.add('hidden');
        }
        row.classList.remove('cart-items__table-row--error');
      }
    });

    // Toggle checkout button and global error banner
    const checkoutBtn = document.querySelector('[ref="checkoutButton"]');
    const errorBanner = document.querySelector('[ref="quantityErrorBanner"]');

    if (hasErrors) {
      checkoutBtn?.setAttribute('disabled', 'disabled');
      if (errorBanner instanceof HTMLElement) {
        errorBanner.classList.remove('hidden');
      }
    } else {
      // Only re-enable if cart is not empty
      if (rows.length > 0) {
        checkoutBtn?.removeAttribute('disabled');
      }
      if (errorBanner instanceof HTMLElement) {
        errorBanner.classList.add('hidden');
      }
    }
  }

  /**
   * Handles form submission - blocks checkout if there are quantity validation errors.
   * @param {SubmitEvent} event - The submit event.
   */
  #handleFormSubmit = (event) => {
    // Check if there are any quantity validation errors
    const hasErrors = this.#hasQuantityErrors();
    if (hasErrors) {
      event.preventDefault();
      event.stopPropagation();
      // Re-run validation to ensure error UI is visible
      this.#validateQuantityRules();
    }
  };

  /**
   * Checks if there are any quantity validation errors without updating UI.
   * @returns {boolean} True if there are validation errors.
   */
  #hasQuantityErrors() {
    const rows = this.querySelectorAll('[ref="cartItemRows[]"]');
    for (const row of rows) {
      if (!(row instanceof HTMLElement)) continue;

      const minQty = parseInt(row.dataset.minQuantity, 10) || 1;
      const increment = parseInt(row.dataset.increment, 10) || 1;

      // Skip items with no pricing tier restrictions
      if (minQty === 1 && increment === 1) continue;

      const input = row.querySelector('input[type="number"]');
      const qty = parseInt(input instanceof HTMLInputElement ? input.value : '0', 10) || 0;

      // Skip removed items
      if (qty === 0) continue;

      const isValid = qty >= minQty && (qty === minQty || (minQty % increment !== 0 ? qty % increment === 0 : (qty - minQty) % increment === 0));
      if (!isValid) return true;
    }
    return false;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();

        // Re-validate quantity rules after morph
        this.#validateQuantityRules();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();

      // Re-validate quantity rules after morph
      this.#validateQuantityRules();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Snaps quantity to nearest valid value based on min and increment rules.
   * @param {number} qty - Current quantity.
   * @param {number} minQty - Minimum quantity required.
   * @param {number} increment - Quantity increment.
   * @returns {number} The snapped valid quantity.
   */
  #snapToValidQuantity(qty, minQty, increment) {
    if (qty < minQty) return minQty;
    if (minQty % increment !== 0) {
      // Min doesn't align with increment (e.g., min=1, increment=10)
      // Valid: minQty itself, or multiples of increment
      if (qty === minQty) return minQty;
      const snapped = Math.round(qty / increment) * increment;
      return snapped < minQty ? minQty : snapped;
    }
    // Standard case: round to nearest valid increment from min
    const steps = Math.round((qty - minQty) / increment);
    return minQty + steps * increment;
  }

  /**
   * Validates quantity rules for all cart items and updates UI accordingly.
   * Shows error messages for invalid quantities and disables checkout if needed.
   */
  #validateQuantityRules() {
    // Query rows directly from DOM since refs may be stale after morphSection
    const rows = this.querySelectorAll('[ref="cartItemRows[]"]');
    let hasErrors = false;

    // Clear any pending auto-correct timeout
    if (this.#autoCorrectTimeout) {
      clearTimeout(this.#autoCorrectTimeout);
      this.#autoCorrectTimeout = null;
    }

    /** @type {{ line: number, snappedQty: number }[]} */
    const itemsToCorrect = [];

    rows.forEach((row, index) => {
      if (!(row instanceof HTMLElement)) return;

      const minQty = parseInt(row.dataset.minQuantity, 10) || 1;
      const increment = parseInt(row.dataset.increment, 10) || 1;
      const input = row.querySelector('input[type="number"]');
      const qty = parseInt(input instanceof HTMLInputElement ? input.value : '0', 10) || 0;
      // Query error element directly since refs may be stale after morphSection
      const errorEl = this.querySelector(`[ref="cartItemQuantityError-${index + 1}"]`);
      const line = index + 1;

      // Skip items with minQty=1 and increment=1 (no pricing tier restrictions)
      if (minQty === 1 && increment === 1) {
        if (errorEl instanceof HTMLElement) {
          errorEl.classList.add('hidden');
        }
        row.classList.remove('cart-items__table-row--error');
        return;
      }

      // Skip removed items (qty=0)
      if (qty === 0) {
        if (errorEl instanceof HTMLElement) {
          errorEl.classList.add('hidden');
        }
        row.classList.remove('cart-items__table-row--error');
        return;
      }

      const isValid = qty >= minQty && (qty === minQty || (minQty % increment !== 0 ? qty % increment === 0 : (qty - minQty) % increment === 0));

      if (!isValid) {
        hasErrors = true;
        if (errorEl instanceof HTMLElement) {
          errorEl.classList.remove('hidden');
        }
        row.classList.add('cart-items__table-row--error');

        // Queue for auto-correction
        const snappedQty = this.#snapToValidQuantity(qty, minQty, increment);
        itemsToCorrect.push({ line, snappedQty });
      } else {
        if (errorEl instanceof HTMLElement) {
          errorEl.classList.add('hidden');
        }
        row.classList.remove('cart-items__table-row--error');
      }
    });

    // Toggle checkout button and global error banner
    const checkoutBtn = document.querySelector('[ref="checkoutButton"]');
    const errorBanner = document.querySelector('[ref="quantityErrorBanner"]');

    if (hasErrors) {
      checkoutBtn?.setAttribute('disabled', 'disabled');
      if (errorBanner instanceof HTMLElement) {
        errorBanner.classList.remove('hidden');
      }

      // Auto-correct invalid quantities after a delay so user sees the error first
      if (itemsToCorrect.length > 0) {
        this.#autoCorrectTimeout = setTimeout(() => {
          // Only correct the first invalid item to avoid race conditions
          const firstItem = itemsToCorrect[0];
          if (firstItem) {
            this.updateQuantity({
              line: firstItem.line,
              quantity: firstItem.snappedQty,
              action: 'change',
            });
          }
        }, 1500);
      }
    } else {
      // Only re-enable if cart is not empty
      if (rows.length > 0) {
        checkoutBtn?.removeAttribute('disabled');
      }
      if (errorBanner instanceof HTMLElement) {
        errorBanner.classList.add('hidden');
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
